/**
 * NWS reflectivity color ramp — a straight port of the validated reference
 * renderer (scratchpad/render.py `render_native`): a continuous piecewise-
 * linear interpolation through the classic 5 dBZ breakpoint colors, with
 * muted low-end grays below 5 dBZ so clear-air structure is visible.
 *
 * Quantization (data-model.md / render.py): raw byte v, dBZ = (v-1)/2 - 32,
 * v === 0 is nodata (fully transparent).
 */

export type RGBA = [number, number, number, number];

const NWS_HEX = [
  "#04e9e7", "#019ff4", "#0300f4", "#02fd02", "#01c501", "#008e00",
  "#fdf802", "#e5bc00", "#fd9500", "#fd0000", "#d40000", "#bc0000",
  "#f800fd", "#9854c6", "#fdfdfd",
];

function hexRgba(hex: string, a = 255): RGBA {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16), a];
}

/** (dBZ, RGBA) breakpoints, low to high — identical set/order to render.py. */
const STOPS: Array<[number, RGBA]> = [
  [-32.0, hexRgba("#2f3542", 80)],
  [0.0, hexRgba("#556075", 140)],
  [5.0, hexRgba("#04e9e7")],
  ...NWS_HEX.slice(1).map((hex, i): [number, RGBA] => [10.0 + 5.0 * i, hexRgba(hex)]),
];

const STOP_X = STOPS.map(([x]) => x);
const STOP_C: [number[], number[], number[], number[]] = [0, 1, 2, 3].map((ch) =>
  STOPS.map(([, c]) => c[ch]),
) as [number[], number[], number[], number[]];

/** np.interp semantics: clamp to the edge value outside [xs[0], xs[last]]. */
function interp1d(x: number, xs: number[], ys: number[]): number {
  if (x <= xs[0]) return ys[0];
  const last = xs.length - 1;
  if (x >= xs[last]) return ys[last];
  for (let i = 0; i < last; i++) {
    if (x <= xs[i + 1]) {
      const t = (x - xs[i]) / (xs[i + 1] - xs[i]);
      return ys[i] + t * (ys[i + 1] - ys[i]);
    }
  }
  return ys[last];
}

export function decodeDbz(v: number): number {
  return (v - 1) / 2 - 32;
}

/** Inverse of decodeDbz, for tests/UI that want to go from dBZ to a raw byte. */
export function dbzToValue(dbz: number): number {
  return Math.round((dbz + 32) * 2 + 1);
}

/** Exported for mosaicGlPalette.ts's continuous-palette LUT (mosaicGlShader.ts's
 * "continuous" palette toggle) — the single source of truth for the dBZ ramp,
 * reused directly rather than re-derived, same discipline as this file's own
 * PALETTE_LUT build loop just below. */
export function colorForDbz(dbz: number): RGBA {
  // np.interp + astype(uint8) truncates; our stop values are all
  // non-negative, so Math.floor matches exactly.
  return [
    Math.floor(interp1d(dbz, STOP_X, STOP_C[0])),
    Math.floor(interp1d(dbz, STOP_X, STOP_C[1])),
    Math.floor(interp1d(dbz, STOP_X, STOP_C[2])),
    Math.floor(interp1d(dbz, STOP_X, STOP_C[3])),
  ];
}

/** 256-entry RGBA lookup table over the full uint8 domain (v=0 -> transparent). */
export const PALETTE_LUT: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(256 * 4);
  for (let v = 1; v < 256; v++) {
    const [r, g, b, a] = colorForDbz(decodeDbz(v));
    lut[v * 4] = r;
    lut[v * 4 + 1] = g;
    lut[v * 4 + 2] = b;
    lut[v * 4 + 3] = a;
  }
  // v === 0 stays [0,0,0,0] (nodata / transparent), already zeroed by construction.
  return lut;
})();

export function colorForValue(v: number): RGBA {
  const i = v * 4;
  return [PALETTE_LUT[i], PALETTE_LUT[i + 1], PALETTE_LUT[i + 2], PALETTE_LUT[i + 3]];
}
